/**
 * Custom hook to sync borrower representative draft data with Redux store
 * Consolidates all Redux dispatch operations for representative data
 */

import { useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  hydrateRepState,
  ShareholderDirector,
} from "../../../features/merchant/rep";

export interface BorrowerRepDraftData {
  repFirstName: string;
  repLastName: string;
  userFirstName: string;
  userLastName: string;
  repEmail: string;
  repTitle: string;
  merchantTitle: string;
  merchantFirstName?: string;
  merchantLastName?: string;
  authChecked: boolean;
  directorChecked: boolean;
  shareHoldersDirectors: ShareholderDirector[];
  numOfShareHoldersDirectors?: number;
}

/**
 * Hook to sync borrower representative draft data with Redux store
 * Uses hydrateRepState for efficient bulk updates
 */
export const useBorrowerRepDraftSync = () => {
  const dispatch = useDispatch();

  /**
   * Sync all representative data to Redux store
   * Uses hydrateRepState with normalized field names
   */
  const syncBorrowerRepToRedux = useCallback(
    (data: BorrowerRepDraftData) => {
      dispatch(
        hydrateRepState({
          repFirstName: data.repFirstName,
          repLastName: data.repLastName,
          userFirstName: data.userFirstName,
          userLastName: data.userLastName,
          repEmail: data.repEmail,
          repTitle: data.repTitle,
          merchantTitle: data.merchantTitle,
          merchantFirstName: data.merchantFirstName || data.userFirstName,
          merchantLastName: data.merchantLastName || data.userLastName,
          authChecked: data.authChecked,
          directorChecked: data.directorChecked,
          shareholdersDirectors: data.shareHoldersDirectors,
          numOfShareholdersDirectors:
            data.numOfShareHoldersDirectors || data.shareHoldersDirectors.length,
        })
      );
    },
    [dispatch]
  );

  return { syncBorrowerRepToRedux };
};
